import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
    Wallet,
    TrendingUp,
    Clock,
    AlertTriangle,
    Info,
    Utensils,
    ShieldAlert,
    ChevronDown,
    LogOut,
    Bell,
    Loader2,
    SlidersHorizontal,
} from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { RechargeFlow } from "@/components/RechargeFlow";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { api } from "@/lib/api";
import { formatUSD, formatDateTime } from "@/lib/format";

const BIOFOOD_LOGO = "/biofood-blue.png";

const RESTRICTION_LABELS = {
    gluten: "Sin gluten",
    lacteos: "Sin lácteos",
    frutos_secos: "Sin frutos secos",
    vegetariano: "Vegetariano",
};

export default function ParentPortal() {
    const { user, logout } = useAuth();
    const { t } = useLang();
    const [children, setChildren] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recharge, setRecharge] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [threshold, setThreshold] = useState(5);
    const [savingThreshold, setSavingThreshold] = useState(false);

    useEffect(() => {
        api.get("/parent/alert-settings")
            .then(({ data }) => setThreshold(data.low_balance_threshold))
            .catch(() => {});
    }, []);

    const saveThreshold = async () => {
        setSavingThreshold(true);
        try {
            const { data } = await api.put("/parent/alert-settings", { low_balance_threshold: threshold });
            setThreshold(data.low_balance_threshold);
            toast.success("Umbral de alerta actualizado");
            setAlertOpen(false);
        } catch (e) {
            toast.error("No se pudo guardar el umbral");
        } finally {
            setSavingThreshold(false);
        }
    };

    useEffect(() => {
        api.get("/parent/children").then(({ data }) => {
            setChildren(data);
            if (data.length) setActiveId(data[0].id);
        });
    }, []);

    const loadDetail = useCallback(async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/parent/students/${id}`);
            setDetail(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeId) loadDetail(activeId);
    }, [activeId, loadDetail]);

    const student = detail?.student;
    const activeChild = children.find((c) => c.id === activeId);

    const onRecharged = (newBalance) => {
        setDetail((d) => (d ? { ...d, student: { ...d.student, balance: newBalance } } : d));
        setChildren((cs) => cs.map((c) => (c.id === activeId ? { ...c, balance: newBalance } : c)));
    };

    const alertIcon = { warning: AlertTriangle, error: ShieldAlert, info: Info };
    const alertStyle = {
        warning: "bg-[#FEF6E9] text-[#B36B00]",
        error: "bg-[#FDECEA] text-[#B71C1C]",
        info: "bg-[#EEF7FC] text-bio-primary",
    };

    const limitPct = student ? Math.min(100, Math.round(((detail.today_spent || 0) / student.daily_limit) * 100)) : 0;

    return (
        <div className="min-h-screen bg-bio-bg font-poppins text-bio-text pb-24">
            {/* Topbar */}
            <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur-md border-b border-bio-border-subtle">
                <div className="h-full px-4 md:px-6 max-w-3xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-8 w-auto" />
                        <span className="hidden sm:block h-5 w-px bg-bio-border-subtle" />
                        <p className="hidden sm:block text-[11px] text-bio-text-secondary font-medium">{t("portal.title")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <LanguageToggle />
                        <button className="h-9 w-9 rounded-full hover:bg-bio-bg-alt flex items-center justify-center text-bio-text-secondary" aria-label="Notificaciones"><Bell className="h-5 w-5" /></button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="user-menu" className="h-9 w-9 rounded-full bg-bio-gradient text-white font-bold text-sm flex items-center justify-center">
                                    {(user?.name || "P").charAt(0)}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-bio-card">
                                <div className="px-3 py-2">
                                    <p className="text-sm font-semibold text-bio-text">{user?.name}</p>
                                    <p className="text-xs text-bio-text-muted">{user?.email}</p>
                                </div>
                                <DropdownMenuItem data-testid="alert-settings-btn" onClick={() => setAlertOpen(true)} className="cursor-pointer">
                                    <SlidersHorizontal className="h-4 w-4 mr-2" /> Configurar alertas
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-bio-error cursor-pointer">
                                    <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
                <DialogContent className="rounded-bio-card">
                    <DialogHeader>
                        <DialogTitle>Alertas de saldo bajo</DialogTitle>
                        <DialogDescription>Te avisamos por correo cuando el saldo de un hijo baje de este monto.</DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <div className="flex items-baseline justify-between mb-3">
                            <span className="text-sm font-medium text-bio-text-secondary">Umbral de alerta</span>
                            <span data-testid="alert-threshold-value" className="text-2xl font-bold text-bio-primary">{formatUSD(threshold)}</span>
                        </div>
                        <Slider data-testid="alert-threshold-slider" min={0} max={20} step={0.5} value={[threshold]} onValueChange={(v) => setThreshold(v[0])} />
                        <div className="flex justify-between text-xs text-bio-text-muted mt-2"><span>$0</span><span>$20</span></div>
                    </div>
                    <DialogFooter>
                        <BioButton data-testid="alert-threshold-save" onClick={saveThreshold} loading={savingThreshold} className="w-full">Guardar</BioButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <main className="max-w-3xl mx-auto px-4 md:px-6 py-5 space-y-5">
                {/* Child selector */}
                {children.length > 1 && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button data-testid="child-selector" className="w-full flex items-center justify-between bg-white rounded-bio-card border border-bio-border-subtle px-4 py-3 shadow-bio-sm">
                                <span className="flex items-center gap-3">
                                    <img src={activeChild?.photo} alt="" className="h-9 w-9 rounded-full bg-bio-bg-alt" />
                                    <span className="text-left">
                                        <span className="block text-sm font-semibold text-bio-text">{activeChild?.name}</span>
                                        <span className="block text-xs text-bio-text-muted">{activeChild?.grade}</span>
                                    </span>
                                </span>
                                <ChevronDown className="h-4 w-4 text-bio-text-muted" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] rounded-bio-card">
                            {children.map((c) => (
                                <DropdownMenuItem key={c.id} data-testid={`child-${c.id}`} onClick={() => setActiveId(c.id)} className="cursor-pointer gap-3">
                                    <img src={c.photo} alt="" className="h-8 w-8 rounded-full bg-bio-bg-alt" />
                                    <span>
                                        <span className="block text-sm font-semibold">{c.name}</span>
                                        <span className="block text-xs text-bio-text-muted">{c.grade} · {formatUSD(c.balance)}</span>
                                    </span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}

                {loading || !student ? (
                    <div className="space-y-5">
                        <Skeleton className="h-44 w-full rounded-bio-lg" />
                        <div className="grid grid-cols-2 gap-4"><Skeleton className="h-24 rounded-bio-card" /><Skeleton className="h-24 rounded-bio-card" /></div>
                        <Skeleton className="h-40 w-full rounded-bio-card" />
                    </div>
                ) : (
                    <>
                        {/* Balance hero */}
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="rounded-bio-lg bg-bio-hero text-white p-6 shadow-bio-modal relative overflow-hidden">
                            <div className="flex items-center gap-4">
                                <img src={student.photo} alt={student.name} className="h-16 w-16 rounded-full ring-4 ring-white/25 bg-white/10" />
                                <div className="min-w-0">
                                    <p className="font-bold text-lg leading-tight truncate">{student.name}</p>
                                    <p className="text-white/70 text-sm">{student.grade} · {detail.cafeteria?.name}</p>
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-white/70 text-xs uppercase tracking-wider font-semibold">{t("portal.balance")}</p>
                                <p className="text-4xl font-extrabold tracking-tight mt-1" data-testid="balance-amount">{formatUSD(student.balance)}</p>
                            </div>
                        </motion.div>

                        {/* Always-visible recharge button */}
                        <BioButton data-testid="recharge-open" className="w-full bg-white bg-none !text-bio-primary hover:bg-white shadow-bio-card h-14 text-base" onClick={() => setRecharge(true)}>
                            <Wallet className="h-5 w-5" /> {t("portal.recharge")}
                        </BioButton>

                        {/* Alerts */}
                        {detail.alerts?.length > 0 && (
                            <div className="space-y-2">
                                {detail.alerts.map((a, i) => {
                                    const Icon = alertIcon[a.type] || Info;
                                    return (
                                        <div key={i} data-testid={`alert-${a.type}`} className={`flex items-center gap-2.5 rounded-bio px-4 py-3 text-sm font-medium ${alertStyle[a.type]}`}>
                                            <Icon className="h-4 w-4 shrink-0" /> {a.text}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Today + limit */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded-bio-card border border-bio-border-subtle p-5 shadow-bio-sm">
                                <div className="flex items-center gap-2 text-bio-text-secondary"><TrendingUp className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">{t("portal.todaySpent")}</span></div>
                                <p className="text-2xl font-extrabold text-bio-text mt-2">{formatUSD(detail.today_spent)}</p>
                            </div>
                            <div className="bg-white rounded-bio-card border border-bio-border-subtle p-5 shadow-bio-sm">
                                <div className="flex items-center justify-between text-bio-text-secondary">
                                    <span className="text-xs font-semibold uppercase tracking-wider">{t("portal.dailyLimit")}</span>
                                    <span className="text-xs font-bold text-bio-text">{formatUSD(student.daily_limit)}</span>
                                </div>
                                <div className="mt-3 h-2 rounded-full bg-bio-bg-alt overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${limitPct >= 100 ? "bg-bio-error" : "bg-bio-primary"}`} style={{ width: `${limitPct}%` }} />
                                </div>
                                <p className="text-xs text-bio-text-muted mt-2">{limitPct}% {t("portal.used")}</p>
                            </div>
                        </div>

                        {/* Restrictions */}
                        {student.restrictions?.length > 0 && (
                            <div className="bg-white rounded-bio-card border border-bio-border-subtle p-5 shadow-bio-sm">
                                <p className="text-sm font-bold text-bio-text mb-3">{t("portal.restrictions")}</p>
                                <div className="flex flex-wrap gap-2">
                                    {student.restrictions.map((r) => (
                                        <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#FEF3C7] text-[#B45309]">
                                            <ShieldAlert className="h-3 w-3" /> {RESTRICTION_LABELS[r] || r}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Menu today */}
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle p-5 shadow-bio-sm">
                            <div className="flex items-center gap-2 mb-4"><Utensils className="h-4 w-4 text-bio-primary" /><p className="text-sm font-bold text-bio-text">{t("portal.menu")}</p></div>
                            {detail.menu?.length ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {detail.menu.slice(0, 6).map((m) => (
                                        <div key={m.id} className="rounded-bio border border-bio-border-subtle overflow-hidden">
                                            <img src={m.image} alt={m.name} className="h-20 w-full object-cover" />
                                            <div className="p-2.5">
                                                <p className="text-xs font-semibold text-bio-text leading-tight line-clamp-2">{m.name}</p>
                                                <p className="text-xs text-bio-primary font-bold mt-1">{formatUSD(m.price)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-bio-text-muted">{t("portal.noMenu")}</p>
                            )}
                        </div>

                        {/* Recent transactions */}
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle p-5 shadow-bio-sm">
                            <div className="flex items-center gap-2 mb-4"><Clock className="h-4 w-4 text-bio-primary" /><p className="text-sm font-bold text-bio-text">{t("portal.recent")}</p></div>
                            {detail.transactions?.length ? (
                                <div className="divide-y divide-bio-border-subtle">
                                    {detail.transactions.map((t) => (
                                        <div key={t.id} className="flex items-center justify-between gap-3 py-3" data-testid="txn-row">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-bio-text truncate">{t.description}</p>
                                                <p className="text-xs text-bio-text-muted">{formatDateTime(t.created_at)} · {t.type === "recharge" ? "Recarga" : (t.method === "qr" ? "QR" : "Huella")}</p>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className={`text-sm font-bold ${t.type === "recharge" ? "text-bio-success" : "text-bio-text"}`}>
                                                    {t.type === "recharge" ? "+" : "−"}{formatUSD(t.amount)}
                                                </span>
                                                <StatusBadge status={t.status} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <EmptyState title={t("portal.noTxns")} description={t("portal.noTxnsSub")} />
                            )}
                        </div>
                    </>
                )}
            </main>

            {student && (
                <RechargeFlow open={recharge} onOpenChange={setRecharge} student={student} onSuccess={onRecharged} />
            )}
        </div>
    );
}
